import { LitElement, html, css } from 'lit';

export class SaveButton extends LitElement {
	static styles = [
		css`
			:host {
				display: flex;
			}
			.save {
				position: relative;
				width: 100%;
				height: 100%;
			}
			input {
				width: 100%;
				height: 100%;
				z-index: 1;
				margin: 0;
				border: none;
				position: relative;
				background: transparent;
				-webkit-appearance: none;
				-moz-appearance: none;
				appearance: none;
				-webkit-tap-highlight-color: transparent;
				cursor: pointer;
			}

			input:focus {
				outline: 0;
			}

			svg {
				position: absolute;
				fill: transparent;
				stroke: currentColor;
				left: 0;
				width: 100%;
				height: 100%;
			}

			@keyframes liked {
				0% {
					transform: scale(0.8);
				}
				50% {
					transform: scale(1.1);
				}
				100% {
					transform: scale(1);
				}
			}

			slot {
				cursor: pointer;
			}

			input:checked + svg,
			input[checked] + svg,
			input[checked='true'] + svg {
				color: #ff0200;
				fill: #ff0200;
				stroke: #ff0200;
				animation: liked 350ms ease-in-out;
			}
		`,
	];

	static properties = {
		productId: { type: Number, attribute: 'product-id' },
		variantId: { type: Number, attribute: 'variant-id', reflect: true },
		handle: { type: String },
		checked: { type: Boolean, reflect: true },
		ariaChecked: { type: String, reflect: true, attribute: 'aria-checked' },
		productImage: { type: String, attribute: 'product-image' },
		productTitle: { type: String, attribute: 'product-title' },
		vendor: { type: String },
		type: { type: String },
	};

	updated() {
		this.items = this._getItems();
		this._setChecked(this.checked);
	}

	_getCustomerId() {
		return window.customer || sessionStorage.getItem('_hos-customer-id');
	}

	_setChecked(force, data) {
		const input = this.shadowRoot.querySelector('input[type="checkbox"]');
		this.checked = force ?? this._isSaved(data);
		this.ariaChecked = this.checked;
		if (input) input.checked = this.checked;
	}

	_isSaved(data) {
		const list = data ?? this.items;
		return list.some((el) => el.variantId == this.variantId);
	}

	_setItems(items) {
		this.items = items;
		localStorage.setItem('_hos-saved-list', JSON.stringify(items));
	}

	_getItems() {
		const localData = localStorage.getItem('_hos-saved-list');
		return localData ? JSON.parse(localData) : [];
	}

	async connectedCallback() {
		super.connectedCallback();
		this.items = this.items ?? this._getItems();
		this.productTitle = this.productTitle ?? '';
		this.vendor = this.vendor ?? '';
		this._setChecked();
		this.type = this.type ?? 'Save Button';

		window.MicroBus.on(`saved-input-toggle:${this.variantId}`, ({ detail }) => {
			const { checked } = detail;
			this.items = this._getItems();
			if (checked === this._isSaved()) {
				this._setChecked(checked, this.items);
			} else {
				this._toggle(null, checked);
			}
		});
	}

	render() {
		return html`
			<slot @click=${this._toggle} @keydown=${this._onKeydown}>
				<div class="save">
					<input type="checkbox" ?checked=${this.checked} />
					<svg
						width="16"
						height="15"
						viewBox="0 0 16 15"
						fill="currentColor"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							fill-rule="evenodd"
							clip-rule="evenodd"
							d="M8.26644 2.72415C9.62289 1.38824 11.7987 1.38824 13.1551 2.72415C14.6819 4.2288 14.6819 6.69457 13.1551 8.19929L8.00043 13.2777L7.52747 12.8118L2.84586 8.19935C1.3183 6.69471 1.3183 4.22953 2.8459 2.72398C4.20218 1.38824 6.37733 1.38824 7.73355 2.72392L8.00023 2.9865L8.26644 2.72415Z"
							stroke-width="1.5"
						/>
					</svg>
				</div>
			</slot>
		`;
	}

	_onKeydown(e) {
		if (e.keyCode === 13 || e.key === 'Enter') {
			this._toggle();
		}
	}

	_toggle(e, force) {
		const el = this;
		this.items = this._getItems();

		this.checked = force !== undefined ? force : !this.checked;
		this._setChecked(this.checked);
		if (this.checked && window?.gtag && this.type !== 'remove') {
			window.gtag('event', `Wishlist - Save on ${this.type}`, {
				event_detail: this.productTitle,
				productId: this.productId,
				variantId: this.variantId,
			});
		} else if (!this.checked && window?.gtag) {
			window.gtag('event', `Wishlist - Remove Saved Item`, {
				event_detail: this.productTitle,
				productId: this.productId,
				variantId: this.variantId,
			});
		}

		if (this.checked && !this._isSaved()) {
			let temp = {
				productId: this.productId,
				variantId: this.variantId,
				handle: this.handle,
			};
			this.items.push(temp);
		} else {
			this.items = this.items.filter((el) => el.variantId !== this.variantId);
		}

		this._setItems(this.items);

		window.MicroBus.emit(`saved-input-toggle:${this.variantId}`, {
			checked: this._isSaved(),
			id: this.id,
			items: this.items,
			handle: this.handle,
		});

		window.MicroBus.emit(`save-items:change`, {
			variantId: this.variantId,
			productId: this.productId,
			handle: this.handle,
			items: this.items,
			count: this.items.length,
		});

		var event = new Event('change', {
			view: window,
			bubbles: true,
			cancelable: true,
		});
		el.dispatchEvent(event);
	}
}
if (!customElements.get('save-button'))
	customElements.define('save-button', SaveButton);
